import { Button, cn, useTranslation } from '@hbs-us/ui';

import type { NotificationListItem } from './contracts.js';
import { useMarkReadMutation, useNotificationsQuery } from './useNotificationsQuery.js';

/**
 * The in-app notification center (Surface C-web — D-13): the bell popover body. Lists each
 * `NotificationItem` (unread = subtle `bg-chart-2/8` tint + filled blue dot; read = hollow dot),
 * with mark-one-read + "Mark all read". Opening the panel does NOT auto-mark-read (explicit only).
 * An empty state shows "You're all caught up". The SINGLE `aria-live="polite"` arrival announcer
 * lives on the bell (NotificationBell), NOT here — it must stay mounted when the panel is closed so
 * a live burst is still announced (NOTIF-05 / D-15). Token-driven, zero hardcoded hex; mono times.
 */

/** Render a relative timestamp in the active locale (now · {n}m · {n}h · Yesterday · date). */
function useRelativeTime(): (iso: string) => string {
  const { t, i18n } = useTranslation('notifications');
  return (iso: string) => {
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const diffMs = Date.now() - then;
    const minutes = Math.floor(diffMs / 60_000);
    if (minutes < 1) return t('relativeTime.now');
    if (minutes < 60) return t('relativeTime.minutes', { count: minutes });
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return t('relativeTime.hours', { count: hours });
    if (hours < 48) return t('relativeTime.yesterday');
    return new Date(iso).toLocaleDateString(i18n.language);
  };
}

interface NotificationItemProps {
  item: NotificationListItem;
  relativeTime: (iso: string) => string;
  onMarkRead: (id: number) => void;
}

function NotificationItem({ item, relativeTime, onMarkRead }: NotificationItemProps) {
  const { t } = useTranslation('notifications');
  const isUnread = item.state !== 'read';

  return (
    <li
      className={cn(
        'flex flex-col gap-1 rounded-md p-3 text-sm',
        isUnread ? 'bg-chart-2/8' : 'bg-transparent',
      )}
    >
      <div className="flex items-start gap-2">
        <span
          aria-hidden="true"
          className={cn(
            'mt-1.5 size-2 shrink-0 rounded-full',
            isUnread ? 'bg-chart-2' : 'border border-muted-foreground',
          )}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="font-semibold text-foreground">{item.title}</span>
          <span className="text-muted-foreground">{item.message}</span>
          {item.buttonLabel && item.buttonUrl ? (
            <a
              href={item.buttonUrl}
              className="mt-1 inline-flex h-11 w-fit items-center rounded-md bg-secondary px-3 text-secondary-foreground text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {item.buttonLabel}
            </a>
          ) : null}
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-muted-foreground text-xs">
              {relativeTime(item.createdAt)}
            </span>
            {isUnread ? (
              <button
                type="button"
                onClick={() => onMarkRead(item.id)}
                className="inline-flex h-11 items-center rounded-md px-2 text-secondary-foreground text-xs underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              >
                {t('center.markRead')}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </li>
  );
}

export function NotificationCenter() {
  const { t } = useTranslation('notifications');
  const relativeTime = useRelativeTime();
  const { data: notifications = [], isLoading } = useNotificationsQuery();
  const markRead = useMarkReadMutation();

  const unreadIds = notifications.filter((n) => n.state !== 'read').map((n) => n.id);
  const hasUnread = unreadIds.length > 0;

  return (
    <div className="flex max-h-[28rem] w-[min(22rem,92vw)] flex-col">
      <div className="flex items-center justify-between gap-3 border-border border-b p-3">
        <h2 className="font-semibold text-base text-foreground">{t('center.heading')}</h2>
        <Button
          variant="ghost"
          size="sm"
          disabled={!hasUnread || markRead.isPending}
          onClick={() => markRead.mutate(unreadIds)}
        >
          {t('center.markAllRead')}
        </Button>
      </div>

      <ul className="flex flex-col gap-1 overflow-auto p-2">
        {!isLoading && notifications.length === 0 ? (
          <li className="flex flex-col items-center gap-1 px-4 py-12 text-center">
            <span className="font-semibold text-foreground text-sm">{t('empty.heading')}</span>
            <span className="text-muted-foreground text-sm">{t('empty.body')}</span>
          </li>
        ) : (
          notifications.map((item) => (
            <NotificationItem
              key={item.id}
              item={item}
              relativeTime={relativeTime}
              onMarkRead={(id) => markRead.mutate([id])}
            />
          ))
        )}
      </ul>
    </div>
  );
}
